import React, { useRef, useEffect } from "react";

interface OtpCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (code: string) => void;
  disabled?: boolean;
  hasError?: boolean;
  autoFocus?: boolean;
}

export function OtpCodeInput({
  value,
  onChange,
  onComplete,
  disabled = false,
  hasError = false,
  autoFocus = true
}: OtpCodeInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Séparer les 6 chiffres
  const digits = Array.from({ length: 6 }, (_, i) => value[i] || "");

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus({ preventScroll: true });
    }
  }, [autoFocus]);

  const handleInputChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const rawVal = e.target.value.replace(/[^0-9]/g, "");
    if (!rawVal) {
      // Effacement d'un chiffre
      const newDigits = [...digits];
      newDigits[index] = "";
      const newValue = newDigits.join("").slice(0, 6);
      onChange(newValue);
      return;
    }

    // Gestion du copier-coller ou saisie multiple
    if (rawVal.length > 1) {
      const chars = rawVal.slice(0, 6).split("");
      const newDigits = [...digits];
      chars.forEach((ch, idx) => {
        if (index + idx < 6) {
          newDigits[index + idx] = ch;
        }
      });
      const combined = newDigits.join("").slice(0, 6);
      onChange(combined);
      const nextFocus = Math.min(index + chars.length, 5);
      inputRefs.current[nextFocus]?.focus();
      if (combined.length === 6 && onComplete) {
        onComplete(combined);
      }
      return;
    }

    const singleDigit = rawVal[rawVal.length - 1] || "";
    const newDigits = [...digits];
    newDigits[index] = singleDigit;
    const combined = newDigits.join("").slice(0, 6);
    onChange(combined);

    if (singleDigit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (combined.length === 6 && onComplete) {
      onComplete(combined);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (!digits[index] && index > 0) {
        // Reculer vers la case précédente si la case courante est déjà vide
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, 6);
    if (pasted) {
      onChange(pasted);
      const nextFocus = Math.min(pasted.length, 5);
      inputRefs.current[nextFocus]?.focus();
      if (pasted.length === 6 && onComplete) {
        onComplete(pasted);
      }
    }
  };

  return (
    <div className="flex items-center justify-center gap-1.5 sm:gap-2.5 my-2">
      {/* 3 premiers chiffres */}
      <div className="flex items-center gap-1.5 sm:gap-2">
        {[0, 1, 2].map((i) => (
          <input
            key={i}
            ref={(el) => { inputRefs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            pattern="[0-9]*"
            maxLength={6}
            disabled={disabled}
            value={digits[i]}
            onChange={(e) => handleInputChange(i, e)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            className={`w-10 h-13 sm:w-12 sm:h-15 text-xl sm:text-2xl font-bold font-mono text-center text-slate-900 bg-white rounded-xl sm:rounded-2xl border transition-all outline-none select-none shadow-2xs ${
              hasError
                ? "border-rose-400 text-rose-700 focus:border-rose-500 focus:ring-2 focus:ring-rose-100"
                : "border-slate-200/90 hover:border-slate-300 focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
            } ${disabled ? "opacity-50 cursor-not-allowed bg-slate-50" : ""}`}
          />
        ))}
      </div>

      {/* Séparateur subtil 3+3 */}
      <span className="w-1.5 sm:w-2 h-0.5 bg-slate-300 rounded-full mx-0.5 shrink-0" />

      {/* 3 derniers chiffres */}
      <div className="flex items-center gap-1.5 sm:gap-2">
        {[3, 4, 5].map((i) => (
          <input
            key={i}
            ref={(el) => { inputRefs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            disabled={disabled}
            value={digits[i]}
            onChange={(e) => handleInputChange(i, e)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            className={`w-10 h-13 sm:w-12 sm:h-15 text-xl sm:text-2xl font-bold font-mono text-center text-slate-900 bg-white rounded-xl sm:rounded-2xl border transition-all outline-none select-none shadow-2xs ${
              hasError
                ? "border-rose-400 text-rose-700 focus:border-rose-500 focus:ring-2 focus:ring-rose-100"
                : "border-slate-200/90 hover:border-slate-300 focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
            } ${disabled ? "opacity-50 cursor-not-allowed bg-slate-50" : ""}`}
          />
        ))}
      </div>
    </div>
  );
}
